import React, { useEffect, useState } from 'react';
import { systemService } from '../../../services/systemService';
import { Printer, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

import { getStudentFeeStatementSummary } from '../../../services/feeService';
import { createDocumentQrValue, downloadHtmlAsPdf, formatServerDateTime, getServerTimestamp } from '../../../utils/documentPdf';

const FeeStatement = ({ student }) => {
  const [institution, setInstitution] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [unpostedTuition, setUnpostedTuition] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    getServerTimestamp().then(setGeneratedAt).catch(() => {});
  }, []);

  useEffect(() => {
    let isCurrent = true;
    const loadStatement = async () => {
      if (!student?.id && !student?.profile_id) {
        if (isCurrent) {
          setTransactions([]);
          setUnpostedTuition([]);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const [loadedInstitution, statementSummary] = await Promise.all([
          systemService.getInstitutionDetails(),
          getStudentFeeStatementSummary(student),
        ]);
        if (!isCurrent) return;
        setInstitution(loadedInstitution);
        // Keep the rendered statement in the exact order used to calculate
        // the summary (including provisional rows when they are available).
        setTransactions(statementSummary?.statementRows || []);
        setUnpostedTuition([]);
      } catch (error) {
        console.error('Unable to load fee statement:', error);
        if (isCurrent) {
          setTransactions([]);
          setUnpostedTuition([]);
        }
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    loadStatement();
    return () => { isCurrent = false; };
  }, [student?.id, student?.profile_id, student?.program_id, student?.program?.id, student?.academic_level]);

  const handlePrint = () => {
    window.print();
  };

  const calculateStage = (moduleNum) => {
    if (!moduleNum) return 'N/A';
    const mod = Number(moduleNum) || 1;
    const year = Math.ceil(mod / 2);
    const sem = mod % 2 === 0 ? 2 : 1;
    return `Y${year}M${sem}`;
  };

  const handleDownloadPdf = async () => {
    try {
      const element = document.getElementById('fee-statement-document');
      if (!element) return;
      const serverTimestamp = await getServerTimestamp();
      setGeneratedAt(serverTimestamp);
      await downloadHtmlAsPdf({
        element,
        filename: `Fee_Statement_${student?.admission_number || student?.reg_number || 'Student'}.pdf`,
        title: 'Fee Statement',
      });
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert(`PDF Generation Error: ${err.message}. Please check console.`);
    }
  };

  const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return '';
    const num = Number(amount);
    const absNum = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return num < 0 ? `(${absNum})` : absNum;
  };

  const statementRows = [...transactions, ...unpostedTuition]
    .sort((first, second) => new Date(first.transaction_date || 0).getTime() - new Date(second.transaction_date || 0).getTime())
    .reduce((rows, transaction) => {
      const previousBalance = rows.length ? rows[rows.length - 1].running_balance : 0;
      const runningBalance = previousBalance + (Number(transaction.debit_amount) || 0) - (Number(transaction.credit_amount) || 0);
      rows.push({ ...transaction, running_balance: runningBalance });
      return rows;
    }, []);
  const totalDebit = statementRows.reduce((sum, tx) => sum + (Number(tx.debit_amount) || 0), 0);
  const totalCredit = statementRows.reduce((sum, tx) => sum + (Number(tx.credit_amount) || 0), 0);
  const finalBalance = statementRows.length > 0 ? Number(statementRows[statementRows.length - 1].running_balance) : 0;

  const isKuccps = String(student?.admission_type || student?.sponsorship || student?.student_type || '').toLowerCase().includes('kuccps');
  const settlement = isKuccps ? 'KUCCPS' : 'SS';

  return (
    <div className="max-w-4xl mx-auto">
      {/* Action Bar */}
      <div className="flex justify-end gap-3 mb-6 print:hidden">
        <button 
          onClick={handlePrint}
          className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors shadow-sm"
        >
          <Printer className="w-4 h-4" />
          Print
        </button>
        <button 
          onClick={handleDownloadPdf}
          className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 font-medium text-sm transition-colors shadow-sm"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      {/* Document Paper */}
      <div id="fee-statement-document" className="bg-white border shadow-sm p-10 md:p-14 print:p-0 print:border-none print:shadow-none bg-white min-h-[1056px] relative flex flex-col font-serif">
        
        {/* Header Section */}
        <div className="text-center pb-2 mb-6 border-b-2 border-black">
          <div className="flex justify-center mb-2">
            {institution?.logo_url ? (
              <img src={institution.logo_url} alt="Institution Logo" className="h-20 object-contain" />
            ) : (
              <div className="h-20 w-20 bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xl">
                LOGO
              </div>
            )}
          </div>
          <h1 className="text-xl font-bold uppercase tracking-wide text-black mb-1">
            {institution?.name || 'UNIVERSITY OF EMBU'}
          </h1>
          <div className="text-sm text-black leading-tight">
            {institution?.postal_address || institution?.address || ''}<br />
            {institution?.phone || ''}<br />
            {institution?.email && <span className="text-blue-600 underline">{institution.email}</span>}
            {institution?.email && institution?.website && ' / '}
            {institution?.website && <span className="text-blue-600 underline">{institution.website}</span>}
          </div>
          
          <div className="mt-4">
            <h3 className="text-[15px] font-bold uppercase underline text-black">
              FEE STATEMENT
            </h3>
          </div>
        </div>

        {/* Student Details Box */}
        <div className="mb-4">
          <table className="w-full border-collapse border border-black text-[13px] text-black">
            <tbody>
              <tr>
                <td className="font-bold p-1.5 border-r border-black w-1/4 pl-2">Student No:</td>
                <td className="p-1.5 border-r border-black w-1/4 uppercase pl-2">{student?.admission_number || student?.reg_number || 'N/A'}</td>
                <td className="font-bold p-1.5 border-r border-black w-1/6 pl-2">Setlement:</td>
                <td className="p-1.5 uppercase pl-2">{settlement}</td>
              </tr>
              <tr>
                <td className="font-bold p-1.5 border-r border-black pl-2">Name:</td>
                <td className="p-1.5 border-r border-black uppercase pl-2">{student?.full_name || (student?.first_name ? `${student.first_name} ${student.last_name || ''}` : '') || 'N/A'}</td>
                <td className="font-bold p-1.5 border-r border-black pl-2">Stage:</td>
                <td className="p-1.5 uppercase pl-2">{calculateStage(student?.current_module)}</td>
              </tr>
              <tr>
                <td className="font-bold p-1.5 border-r border-black pl-2">Programme:</td>
                <td className="p-1.5 border-r border-black uppercase pl-2">{student?.program?.name || 'MASTER OF SCIENCE IN PURE MATHEMATICS'}</td>
                <td className="font-bold p-1.5 border-r border-black pl-2">Campus:</td>
                <td className="p-1.5 uppercase pl-2">MAIN</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Household Section Table */}
        <div className="flex-1">
          <table className="w-full border-collapse border border-black text-[12px] text-black">
            <thead>
              <tr className="bg-[#a8a8a8]">
                <th colSpan="6" className="border border-black p-1.5 text-left font-bold uppercase text-[15px] pl-2">
                  HOUSEHOLD SECTION
                </th>
              </tr>
              <tr className="bg-[#b4c6e7]">
                <th className="border border-black p-1.5 text-left font-bold w-[12%] pl-2">DATE</th>
                <th className="border border-black p-1.5 text-left font-bold w-[18%] pl-2">DOC. NO.</th>
                <th className="border border-black p-1.5 text-left font-bold w-[40%] pl-2">DESCRIPTION</th>
                <th className="border border-black p-1.5 text-right font-bold w-[10%]">DEBIT</th>
                <th className="border border-black p-1.5 text-right font-bold w-[10%]">CREDIT</th>
                <th className="border border-black p-1.5 text-right font-bold w-[10%]">BALANCE</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
              <tr>
                <td colSpan="6" className="text-center p-4">Loading statement data...</td>
              </tr>
            ) : statementRows.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center p-4 text-gray-500">No transactions found for this student.</td>
              </tr>
            ) : (
              statementRows.map((tx, idx) => (
                <tr key={idx}>
                  <td className="p-2 border-b border-r border-black font-serif text-sm">
                    {new Date(tx.transaction_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </td>
                  <td className="p-2 border-b border-r border-black font-serif text-sm">{tx.doc_no || '-'}</td>
                  <td className="p-2 border-b border-r border-black font-serif text-sm uppercase">
                    {tx.description || '-'}
                  </td>
                  <td className="p-2 border-b border-r border-black text-right font-serif text-sm">
                    {formatMoney(tx.debit_amount)}
                  </td>
                  <td className="p-2 border-b border-r border-black text-right font-serif text-sm">
                    {formatMoney(tx.credit_amount)}
                  </td>
                  <td className="p-2 border-b border-black text-right font-serif text-sm">
                    {formatMoney(tx.running_balance)}
                  </td>
                </tr>
              ))
            )}
              
              {/* Total Row */}
              <tr className="bg-[#e2efd9]">
                <td colSpan="3" className="border border-black p-2 font-bold uppercase text-right pr-4 text-[13px]">
                  TOTAL FOR HOUSEHOLD SECTION
                </td>
                <td className="border border-black p-2 text-right font-bold text-[13px] underline decoration-double">
                  {formatMoney(totalDebit)}
                </td>
                <td className="border border-black p-2 text-right font-bold text-[13px] underline decoration-double">
                  {formatMoney(totalCredit)}
                </td>
                <td className="border border-black bg-[#e2efd9]"></td>
              </tr>
              
              {/* Final Balance Row */}
              <tr>
                <td colSpan="3" className="border-b border-black bg-white"></td>
                <td className="border border-black p-2 text-right font-bold text-[13px] underline decoration-double bg-white">
                  {formatMoney(totalDebit)}
                </td>
                <td className="border border-black p-2 text-right font-bold text-[13px] underline decoration-double bg-white">
                  {formatMoney(totalCredit)}
                </td>
                <td className="border border-black p-2 text-right font-bold text-[13px] underline decoration-double bg-white">
                  {formatMoney(finalBalance)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 pb-2">
          <div className="flex justify-between items-end">
            <div className="text-[13px] text-black">
              Generated By: System Generated
            </div>
            
            <div className="flex flex-col items-center gap-2 -ml-16">
              <div className="w-24 h-24 border-[1.5px] border-black p-1 flex items-center justify-center bg-white">
                <QRCodeSVG 
                  value={createDocumentQrValue({ type: 'fee_statement', documentId: student?.id || student?.profile_id, student, generatedAt, extra: { balance: finalBalance, document: 'Fee Statement' } })}
                  size={88}
                  level="M"
                />
              </div>
            </div>
            
            <div className="text-[13px] text-black text-right">
              {formatServerDateTime(generatedAt)}
            </div>
          </div>
        </div>

      </div>

      <style jsx="true">{`
        @media print {
          body * { visibility: hidden; }
          #root, #root * { visibility: visible; }
          header, nav, aside { display: none !important; }
          .print\\:hidden { display: none !important; }
          
          #fee-statement-document {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 0;
            box-shadow: none;
            border: none;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FeeStatement;
